import React, { memo } from "react";

import {
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { DesktopSystemTableRowProps } from "./DesktopSystemTableRow";
import DesktopSystemTableRow from "./DesktopSystemTableRow";

import type { OverviewSystem } from "./index";

export type DesktopSystemTableProps = {
  isLoading?: boolean;
  systems?: OverviewSystem[];
  onSystemClicked: DesktopSystemTableRowProps["onSystemClicked"];
  onToggleFavorite: DesktopSystemTableRowProps["onToggleFavorite"];
  getSystemType: DesktopSystemTableRowProps["getSystemType"];
};

const styles = {
  tableContainer: {
    maxHeight: 316,
  },
  tableHead: {
    backgroundColor: "grey.200",
  },
};

const DesktopSystemTable = ({
  isLoading = false,
  systems,
  onSystemClicked,
  onToggleFavorite,
  getSystemType,
}: DesktopSystemTableProps) => {
  const { t } = useTranslation("common");
  const hasSystems = (systems?.length ?? 0) > 0;

  return (
    <TableContainer sx={styles.tableContainer} component={Paper}>
      {isLoading && <LinearProgress />}
      <Table size="small" stickyHeader>
        <TableHead sx={styles.tableHead}>
          <TableRow>
            <TableCell align="center" />
            <TableCell align="center">{t("system_name")}</TableCell>
            <TableCell align="center">{t("state_or_province")}</TableCell>
            <TableCell align="center">{t("system_type")}</TableCell>
            <TableCell align="center">{t("regulatory_id")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && !hasSystems ? (
            <TableRow>
              <TableCell align="center" colSpan={5}>
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                  <Typography variant="body2">{t("loading_ellipses")}</Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : (
            systems?.map((system) => (
              <DesktopSystemTableRow
                key={system.id}
                system={system}
                onSystemClicked={onSystemClicked}
                onToggleFavorite={onToggleFavorite}
                getSystemType={getSystemType}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(DesktopSystemTable);
